import {
  ZONE_TYPE_TUPLE_SEPARATOR,
  typeMatchesZoneSelection
} from '@alpinehomeair/common';
import {
  AirHandlerOptionsResponseType,
  CondenserType,
  ZoneTypeTuplesType
} from 'data/types';
import {
  AirHandlerOptionsWithPoolType,
  AirHandlerPoolState,
  AvailabilityCondenser,
  AvailabilityStateCondenser,
  PerZoneTypes,
  RadialDuctPassiveSpacesByZoneType,
  ZoneTypeSelections
} from 'utils/air-handler-availability-types';
import { catalogSelectedAirHandlerType } from 'utils/requires-ducted-air-handler';

export function selectedTypeByZone(
  condenser: Pick<CondenserType, 'zones'>
): ZoneTypeSelections {
  const selections: ZoneTypeSelections = {};
  condenser.zones.forEach((zone) => {
    selections[zone.uniqueId] = catalogSelectedAirHandlerType(
      zone.airHandler?.airHandlerTypeCode ?? null
    );
  });
  return selections;
}

export function derivePerZoneTypesFromTuples(
  tuples: ZoneTypeTuplesType,
  selections: ZoneTypeSelections
): PerZoneTypes {
  const perZoneSets = new Map<string, Set<string>>();
  tuples.tuples.forEach((tuple) => {
    const parts = tuple.split(ZONE_TYPE_TUPLE_SEPARATOR);
    const matches = tuples.zones.every((zoneId, index) =>
      typeMatchesZoneSelection(parts[index] || null, selections[zoneId])
    );
    if (!matches) return;
    tuples.zones.forEach((zoneId, index) => {
      const typeCode = parts[index];
      if (!typeCode) return;
      const set = perZoneSets.get(zoneId) ?? new Set<string>();
      set.add(typeCode);
      perZoneSets.set(zoneId, set);
    });
  });

  const perZoneTypes: PerZoneTypes = {};
  perZoneSets.forEach((set, zoneId) => {
    perZoneTypes[zoneId] = [...set].sort();
  });
  return perZoneTypes;
}

export function getZoneTypeTuples(
  options: AirHandlerOptionsResponseType | null | undefined
): ZoneTypeTuplesType | null {
  return options?.zoneTypeTuples ?? null;
}

export function getAirHandlerPoolState(
  options: AirHandlerOptionsResponseType | null | undefined
): AirHandlerPoolState | null {
  return (
    (options as AirHandlerOptionsWithPoolType | null | undefined)?.pool ?? null
  );
}

export function getRadialDuctPassiveSpaces(
  options: AirHandlerOptionsResponseType | null | undefined
): RadialDuctPassiveSpacesByZoneType | null {
  return (
    (options as AirHandlerOptionsWithPoolType | null | undefined)
      ?.radialDuctPassiveSpaces ?? null
  );
}

export function hasCurrentAirHandlerPool(
  condenser: Pick<CondenserType, 'airHandlerOptions'>
): boolean {
  const pool = getAirHandlerPoolState(condenser.airHandlerOptions);
  return (
    pool !== null &&
    !pool.stale &&
    getZoneTypeTuples(condenser.airHandlerOptions) !== null
  );
}

export function withAirHandlerPoolState(
  options: AirHandlerOptionsResponseType,
  requestedPoolHash: string | undefined,
  currentPoolHash: string | null
): AirHandlerOptionsResponseType {
  if (requestedPoolHash === undefined) return options;
  const pool: AirHandlerPoolState = {
    hash: requestedPoolHash,
    stale: requestedPoolHash !== currentPoolHash
  };
  const withPool: AirHandlerOptionsWithPoolType = { ...options, pool };
  return withPool;
}

export function withAirHandlerPoolStaleness(
  options: AirHandlerOptionsResponseType,
  currentPoolHash: string | null
): AirHandlerOptionsResponseType {
  const pool = getAirHandlerPoolState(options);
  if (!pool) return options;
  const stale = pool.hash !== currentPoolHash;
  if (stale === pool.stale) return options;
  const withPool: AirHandlerOptionsWithPoolType = {
    ...options,
    pool: { ...pool, stale }
  };
  return withPool;
}

export function isAirHandlerAvailabilityUnknown(
  condenser: AvailabilityStateCondenser
): boolean {
  if (condenser.airHandlerOptions == null) return true;
  return !condenser.validSystemsFetched && !hasCurrentAirHandlerPool(condenser);
}

export function isPositionedWithZoneItems(
  condenser: Pick<CondenserType, 'position' | 'zones'>
): boolean {
  return (
    condenser.position !== undefined &&
    condenser.zones.some((zone) => zone.zoneItems.length > 0)
  );
}

export function hasCondenserAwaitingAirHandlerAvailability(
  condensers: ReadonlyArray<AvailabilityStateCondenser>
): boolean {
  return condensers
    .filter(isPositionedWithZoneItems)
    .some(isAirHandlerAvailabilityUnknown);
}

export function getEffectivePerZoneTypes(
  condenser: AvailabilityCondenser
): PerZoneTypes | null {
  const options = condenser.airHandlerOptions;
  if (!options) return null;
  const tuples = getZoneTypeTuples(options);
  if (!tuples || !hasCurrentAirHandlerPool(condenser)) {
    return options.perZoneTypes;
  }
  return derivePerZoneTypesFromTuples(tuples, selectedTypeByZone(condenser));
}
